import React, { useMemo, useState } from 'react';

import { MenuItem, Stack, TextField } from '@mui/material';

import { useGetServiceFees } from '~/hooks/apis/business-traveller/useGetServiceFees';

import {
  CUSTOM_SERVICE_FEE_DROPDOWN_OPTION,
  CustomOfferServiceFeeDropdownOption,
  getCustomOfferServiceFeeOptions,
} from '~/utils/businessTraveller/serviceFee';

const NO_SELECTION_VALUE = '';

interface Props {
  businessId: string;
  value?: App.LebtCustomOfferServiceFee;
  onChange: (fee?: App.LebtCustomOfferServiceFee) => void;
  disabled?: boolean;
}

const toServiceFee = (option: CustomOfferServiceFeeDropdownOption, amount: number): App.LebtCustomOfferServiceFee => ({
  source: option.source,
  charge_type: option.type,
  destination: option.destination,
  product: option.product,
  concierge_mode: option.conciergeMode,
  amount,
});

/**
 * The service fee an agent charges on a Business Traveller import, chosen like the fee of a custom offer:
 * one of the business's call-centre fees, no fee, or a custom amount.
 */
export default function LebtServiceFeePicker({ businessId, value, onChange, disabled }: Props) {
  const { data: serviceFeesResponse, isLoading } = useGetServiceFees(businessId);
  const [selectedKey, setSelectedKey] = useState(NO_SELECTION_VALUE);

  const options = useMemo(
    () => getCustomOfferServiceFeeOptions(serviceFeesResponse?.result ?? [], 'flight'),
    [serviceFeesResponse],
  );
  const selectedOption = selectedKey === NO_SELECTION_VALUE ? undefined : options[Number(selectedKey)];

  const selectOption = (key: string) => {
    setSelectedKey(key);
    if (key === NO_SELECTION_VALUE) {
      onChange(undefined);
      return;
    }
    const option = options[Number(key)];
    if (option.isCustomServiceFee) {
      onChange(toServiceFee(option, value?.amount ?? 0));
      return;
    }
    onChange(toServiceFee(option, option.amount));
  };

  return (
    <Stack direction="row" spacing={2}>
      <TextField
        select
        size="small"
        label="Service fee"
        value={selectedKey}
        onChange={(event) => selectOption(event.target.value)}
        disabled={disabled || isLoading || !businessId}
        required
        sx={{ minWidth: 360 }}
      >
        <MenuItem value={NO_SELECTION_VALUE}>Select a service fee</MenuItem>
        {options.map((option, index) => (
          <MenuItem key={option.label + index} value={String(index)}>
            {option.label}
          </MenuItem>
        ))}
      </TextField>
      {selectedOption?.isCustomServiceFee && (
        <TextField
          size="small"
          type="number"
          label={CUSTOM_SERVICE_FEE_DROPDOWN_OPTION.label}
          value={value?.amount ?? ''}
          onChange={(event) => onChange(toServiceFee(selectedOption, Number(event.target.value)))}
          inputProps={{ min: 0, step: 0.01 }}
          disabled={disabled}
        />
      )}
    </Stack>
  );
}
